// Global Instructions Rule Applied!
//
// The left-nav's current AREA (cfg.menu_sections.key) as explicit state.
//
// Why state and not a derived value: menu items are duplicated across areas
// (deals / funds / fundraising live in both), and record-form routes match no
// menu item at all, so "which area am I in?" cannot be answered from the route
// alone. Deriving it meant two call sites guessing independently — the menu
// body from the path, the footer switcher from the active tab — which is how
// they came to disagree. The area now changes on exactly two events:
//
//   1. the user picks one in the footer switcher  -> setAreaFromUser
//   2. ONCE per session, on a cold load by someone who has never chosen an
//      area, when the landing route resolves to exactly ONE area
//                                                 -> setAreaFromRoute
//
// (2) is a seed, not an ongoing input (owner 2026-09-02). After it has fired
// the route never touches the area again: focusing a tab, opening a record form
// and following a global-search hit all leave the sidebar where the user put
// it. An ambiguous or unmatched route changes nothing either way.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface MenuAreaState {
  /** cfg.menu_sections.key, or null when nothing has decided yet. */
  areaKey: string | null
  /** Provenance of the current value — debugging aid, not read by the UI. */
  source: 'user' | 'route' | null
}

// areaKey starts null rather than at a default on purpose: menu cfg arrives
// asynchronously, and storing a default computed from an empty section list
// would lock in garbage. The fallback to the default area is DERIVED at render
// (resolveDisplayAreaKey), never stored.
const initialState: MenuAreaState = {
  areaKey: null,
  source: null
}

const menuAreaSlice = createSlice({
  name: 'menuArea',
  initialState,
  reducers: {
    /** Explicit pick in the bottom-left area switcher. */
    setAreaFromUser: (state, action: PayloadAction<string>) => {
      state.areaKey = action.payload
      state.source = 'user'
    },
    /** Cold-load seed: the landing route belongs to exactly one area, and the
     *  user has neither picked one nor stored a preference. Fires at most once
     *  per session — see MainTabContainer's areaSeededRef. */
    setAreaFromRoute: (state, action: PayloadAction<string>) => {
      state.areaKey = action.payload
      state.source = 'route'
    },
    /** Logout / user switch — otherwise the next user inherits this area. */
    resetMenuArea: () => initialState
  }
})

export const { setAreaFromUser, setAreaFromRoute, resetMenuArea } = menuAreaSlice.actions

export const selectMenuAreaKey = (state: { menuArea?: MenuAreaState }): string | null =>
  state.menuArea?.areaKey ?? null

export default menuAreaSlice.reducer
